import { CategoryType } from "../models/category";
import { Injectable } from "@angular/core";
import { SharedService } from "./shared.service";
import { StorageService } from "./storage.service";
import { SystemService } from "./system.service";
import { User } from "../models/user";
import { combineLatest, map, ReplaySubject } from "rxjs";

export type MenuCategoryFlags = {
  hasContratanteShows: boolean;
  hasOtherCategory: boolean;
};

@Injectable({
  providedIn: "root",
})
export class DataService {
  private _currentSpeciality?: CategoryType;
  currentCategory$: ReplaySubject<CategoryType | undefined> = new ReplaySubject<
    CategoryType | undefined
  >(1);
  categories?: CategoryType[]; //Level 1
  popularServices?: CategoryType[];

  specialities?: CategoryType[]; //Level 2

  diamondUsers?: User[];
  newUsers: User[];
  userFavoritesIds?: number[];

  //Categoria da tab selecionada
  categoryId: number;

  // Cache das flags de exibição do menu (Perfil Contratante x Especialista)
  menuCategoryFlags: MenuCategoryFlags | null = null;
  private menuCategoryFlagsUserId: string | null = null;

  // Requisição em andamento, para que o pré-aquecimento (home) e um clique no
  // menu compartilhem a mesma chamada em vez de disparar duas.
  private menuCategoryFlagsPending: Promise<MenuCategoryFlags> | null = null;
  private menuCategoryFlagsPendingUserId: string | null = null;

  setMenuCategoryFlags(userId: string, flags: MenuCategoryFlags) {
    this.menuCategoryFlags        = flags;
    this.menuCategoryFlagsUserId  = userId;
  }

  clearMenuCategoryFlags() {
    this.menuCategoryFlags               = null;
    this.menuCategoryFlagsUserId         = null;
    this.menuCategoryFlagsPending        = null;
    this.menuCategoryFlagsPendingUserId  = null;
  }

  getMenuCategoryFlagsFor(userId: string): MenuCategoryFlags | null {
    if (this.menuCategoryFlagsUserId !== userId) return null;
    return this.menuCategoryFlags;
  }

  setMenuCategoryFlagsPending(userId: string, pending: Promise<MenuCategoryFlags>) {
    this.menuCategoryFlagsPending       = pending;
    this.menuCategoryFlagsPendingUserId = userId;
  }

  getMenuCategoryFlagsPendingFor(userId: string): Promise<MenuCategoryFlags> | null {
    if (this.menuCategoryFlagsPendingUserId !== userId) return null;
    return this.menuCategoryFlagsPending;
  }

  constructor(
    public shared: SharedService,
    public system: SystemService,
    public storage: StorageService,
  ) {
    this.listen();
  }

  listen() {
    this.currentCategory$.subscribe((res) => {
      this.currentSpeciality = res;
    });
    this.shared.user$.subscribe((user) => {
      if (user && !this.userFavoritesIds) {
        this.updateFavourites();
      }
      if (!user || user.id !== this.menuCategoryFlagsUserId) {
        this.clearMenuCategoryFlags();
      }
    });
  }

  updateFavourites() {
    this.shared
      .get<number[]>("favourites?id=" + this.shared.user.user)
      .subscribe((res) => {
        if (res && res.data) {
          this.userFavoritesIds = res.data;
        } else {
          this.userFavoritesIds = [];
        }
      });
  }

  get currentSpeciality(): CategoryType | undefined {
    return this._currentSpeciality;
  }

  set currentSpeciality(speciality: CategoryType | undefined) {
    if (!speciality) {
      this._currentSpeciality = undefined;
      return;
    }
    this.system.showLoading();
    this.shared
      .get<{
        results: CategoryType[];
      }>("categories?category_type=2&parent=" + speciality?.id)
      .subscribe((res) => {
        this.system.dismissLoading();
        this._currentSpeciality = speciality;
        if (res && res.data && res.data.results) {
          this.specialities = res.data.results;
        } else {
          this.specialities = [];
        }
      });
  }
  loadData() {
    return combineLatest([
      this.shared.get<{ results: CategoryType[] }>(
        "categories?category_type=1",
      ), //Categorias Base
      this.shared.get<{ results: CategoryType[] }>("categories?popular=1"), //Serviços populares
      this.shared.get<{ results: User[] }>("profiles?handpicked=1"), //Usuários diamantes
      this.shared.get<{
        last_clients: {
          profile: User;
          user: User;
          categories: CategoryType[];
        }[];
      }>("user/last/10/"),
    ]).pipe(
      map(
        ([resCategories, resPopularServices, resDiamondUsers, resNewUsers]) => {
          if (
            resCategories &&
            resCategories.data &&
            resCategories.data.results
          ) {
            this.categories = resCategories.data.results;
          } else {
            this.categories = [];
          }
          if (
            resPopularServices &&
            resPopularServices.data &&
            resPopularServices.data.results
          ) {
            this.popularServices = resPopularServices.data.results.filter(
              (p) => p.category_picture,
            );
          } else {
            this.popularServices = [];
          }
          if (
            resDiamondUsers &&
            resDiamondUsers.data &&
            resDiamondUsers.data.results
          ) {
            this.diamondUsers = resDiamondUsers.data.results
              .filter(
                (u) => u.categories && u.categories[0] && u.categories[0].title,
              )
              .map((u) => {
                u.categoryName = u.categories[0].title; //Adicionando nome de categoria principal do usuário
                return u;
              });
          } else {
            this.diamondUsers = [];
          }
          if (
            resNewUsers &&
            resNewUsers.data &&
            resNewUsers.data.last_clients
          ) {
            this.newUsers = resNewUsers.data.last_clients.map((result) => {
              const user = {
                ...result.user,
                ...result.profile,
              };

              if (
                Array.isArray(result.categories) &&
                result.categories.length > 0 &&
                result.categories[0].category_title
              ) {
                user.categoryName = result.categories[0].category_title;
                user.categoryCode = result.categories[0].code;
                if (!user.main_speciality_price) {
                  user.main_speciality_price =
                    result.categories[0].category_price;
                }
              } else {
                user.categoryName = "";
              }
              return user;
            });
          } else {
            this.newUsers = [];
          }
        },
      ),
    );
  }
}
